/* eslint-disable space-before-function-paren */
/*
 * @Author: Auto
 * @Date: 2025-01-27
 * @Description: 贷款申请弹窗组件
 */
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Alert, Button, Checkbox, Col, Form, Input, message, Modal, Popover, Row, Select, Spin } from 'antd'
import User from '@/modules/user'
import Loan from '@/modules/loan'
import Tax from '@/modules/tax'
import {
  buildLoanApplication,
  getUsageOptions,
  mergeOrgOptions,
  validateLoanAmount
} from '../../loanAdapter'
import TaxAuthModal from './TaxAuthModal'
import Protocol from './Protocol'

const ORG_PAGE_SIZE = '30'

const LoanApplyModal = forwardRef((props, ref) => {
  const [form] = Form.useForm()
  const [userType, setUserType] = useState('')
  const [visible, setVisible] = useState(false)
  const [productData, setProductData] = useState(null)
  const [productOrg, setProductOrg] = useState(null)
  const [usageOptions, setUsageOptions] = useState([])
  const [orgOptions, setOrgOptions] = useState([])
  const [orgPage, setOrgPage] = useState(0)
  const [orgLast, setOrgLast] = useState(false)
  const [orgLoading, setOrgLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [isSending, setIsSending] = useState(false)
  const [taxAuthVisible, setTaxAuthVisible] = useState(false)
  const [verifyingVisible, setVerifyingVisible] = useState(false)
  const [pendingValues, setPendingValues] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [proModalVisible, setProModalVisible] = useState(false)
  const timerRef = useRef(null)
  const orgLoadingRef = useRef(false)
  const submittingRef = useRef(false)

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current)
  }, [])

  /**
   * 加载一页意向机构。ref 锁用于阻止滚动事件在状态更新前发起重复请求。
   *
   * @param {number} page 从 1 开始的页码
   * @param {boolean} replace 是否清空现有选项后写入
   * @returns {Promise<void>}
   */
  const loadOrgPage = async (page, replace = false) => {
    if (orgLoadingRef.current) return
    orgLoadingRef.current = true
    setOrgLoading(true)
    try {
      const result = await Loan.getOrgList({ page: String(page), rows: ORG_PAGE_SIZE })
      setOrgOptions(current => mergeOrgOptions(replace ? [] : current, result?.content || []))
      setOrgPage(page)
      setOrgLast(Boolean(result?.last))
    } catch (error) {
      console.error('加载意向机构失败', error)
    } finally {
      orgLoadingRef.current = false
      setOrgLoading(false)
    }
  }

  /** 释放覆盖身份校验、税务授权和最终提交全过程的防重复提交锁。 */
  const releaseSubmission = () => {
    submittingRef.current = false
    setSubmitting(false)
  }

  const closeAfterSuccess = () => {
    setVisible(false)
    setTaxAuthVisible(false)
    setPendingValues(null)
    form.resetFields()
  }

  useImperativeHandle(ref, () => ({
    open: data => {
      const nextUsageOptions = getUsageOptions(data)
      const nextUserType = ['0', '1'].includes(data?.customerType) ? data.customerType : ''
      const nextProductOrg = data?.productOrg || null
      setProductData(data)
      setUserType(nextUserType)
      setUsageOptions(nextUsageOptions)
      setProductOrg(nextProductOrg)
      setOrgOptions([])
      setOrgPage(0)
      setOrgLast(false)
      setPendingValues(null)
      setTaxAuthVisible(false)
      releaseSubmission()
      form.resetFields()
      form.setFieldsValue({
        userTypeLabel: nextUserType === '0' ? '个人' : nextUserType === '1' ? '企业' : '',
        usageInf: nextUsageOptions.length === 1 ? nextUsageOptions[0].value : undefined,
        orgNos: nextProductOrg?.orgNo ? [nextProductOrg.orgNo] : []
      })
      setVisible(true)
      loadOrgPage(1, true)
    }
  }))

  const handleClose = () => {
    if (submittingRef.current) return
    setVisible(false)
    form.resetFields()
  }

  /**
   * 组装并提交贷款申请。该接口非幂等，调用期间由 submittingRef 全程加锁。
   *
   * @param {Object} values 已通过表单校验的字段
   * @param {string} [authCode] 税务授权流程返回的验证码
   * @returns {Promise<void>}
   */
  const submitLoan = async (values, authCode) => {
    const submitData = buildLoanApplication({
      values,
      product: productData,
      phoneAuthCode: authCode || values.verifyCode
    })
    await Loan.submitLoanApplyForm(submitData)
    message.success('提交成功，请耐心等待！')
    closeAfterSuccess()
  }

  /**
   * 根据产品 customerType 进入个人直提或企业法人校验流程。
   * 企业校验未通过时保留锁和表单值，等待税务授权弹窗继续提交。
   *
   * @param {Object} values 已通过表单校验的字段
   * @returns {Promise<void>}
   */
  const onFinish = async values => {
    if (submittingRef.current) return
    submittingRef.current = true
    setSubmitting(true)
    setPendingValues(values)
    let awaitingTaxAuth = false
    try {
      if (userType === '1') {
        setVerifyingVisible(true)
        const isAuthPassed = await Tax.checkLegalPersonIdentity({
          taxNo: values.taxId,
          taxUserAccount: values.mobile
        })
        if (!isAuthPassed) {
          awaitingTaxAuth = true
          setTaxAuthVisible(true)
          return
        }
      }
      await submitLoan(values)
    } catch (error) {
      console.error('提交贷款申请失败', error)
    } finally {
      setVerifyingVisible(false)
      if (!awaitingTaxAuth) releaseSubmission()
    }
  }

  const handleTaxSuccess = async () => {
    setTaxAuthVisible(false)
    try {
      await submitLoan(pendingValues)
    } catch (error) {
      console.error('提交贷款申请失败', error)
    } finally {
      releaseSubmission()
    }
  }

  const handleTaxCancel = () => {
    setTaxAuthVisible(false)
    releaseSubmission()
  }

  const handleSendCode = async () => {
    try {
      const fieldName = userType === '1' ? 'mobile' : 'borrowerMobile'
      const values = await form.validateFields([fieldName])
      const mobile = values[fieldName]
      if (!mobile) {
        message.error('请输入手机号')
        return
      }

      setIsSending(true)
      await new User().sendCode({ phone: mobile })
      message.success('验证码发送成功')
      setCountdown(60)
      if (timerRef.current) clearInterval(timerRef.current)
      timerRef.current = setInterval(() => {
        setCountdown(previous => {
          if (previous <= 1) {
            clearInterval(timerRef.current)
            return 0
          }
          return previous - 1
        })
      }, 1000)
    } catch (error) {
      console.error('发送验证码失败', error)
    } finally {
      setIsSending(false)
    }
  }

  /** 滚动到机构下拉框底部时加载下一页，末页或请求中不再触发。 */
  const handleOrgPopupScroll = event => {
    const target = event.target
    const atBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 8
    if (atBottom && !orgLast && !orgLoadingRef.current) {
      loadOrgPage(orgPage + 1)
    }
  }

  /** 将意向机构选择限制为最多 3 个，并保留最先选中的机构。 */
  const normalizeOrgValues = values => {
    if (values.length <= 3) return values
    message.warning('意向机构最多选择3个')
    return values.slice(0, 3)
  }

  const renderVerifyCodeItem = () => (
    <Col span={12}>
      <Form.Item label={<span style={{ color: '#666' }}> 手机验证码</span>} required>
        <Row gutter={8}>
          <Col span={16}>
            <Form.Item name='verifyCode' noStyle rules={[{ required: true, message: '请输入验证码' }]}>
              <Input placeholder='请输入验证码' allowClear />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Button type='primary' block onClick={handleSendCode} disabled={isSending || countdown > 0 || submitting}>
              {countdown > 0 ? `${countdown}s` : '获取'}
            </Button>
          </Col>
        </Row>
      </Form.Item>
    </Col>
  )

  const supportedCustomer = ['0', '1'].includes(userType)
  const hasUsage = usageOptions.length > 0
  const submitDisabled = !supportedCustomer || !hasUsage
  const fixedOrg = productOrg?.orgNo ? productOrg : null
  const selectOrgOptions = fixedOrg ? mergeOrgOptions([fixedOrg], orgOptions) : orgOptions

  return (
    <Modal
      title='贷款申请信息填写'
      visible={visible}
      onCancel={handleClose}
      maskClosable={false}
      closable={!submitting}
      footer={null}
      width={1000}
      destroyOnClose
      centered
    >
      {!supportedCustomer && (
        <Alert type='warning' showIcon message='该产品贷款对象暂不支持申请' style={{ marginBottom: 16 }} />
      )}
      {!hasUsage && (
        <Alert type='warning' showIcon message='该产品未配置贷款用途' style={{ marginBottom: 16 }} />
      )}
      <Form
        form={form}
        onFinish={onFinish}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        style={{ marginTop: 20 }}
      >
        <h3 style={{ borderBottom: '2px solid #1890ff', display: 'inline-block', paddingBottom: 5, marginBottom: 20, color: '#333', fontSize: 16, fontWeight: 'bold' }}>基础信息</h3>
        <Row gutter={[24, 0]}>
          <Col span={12}>
            <Form.Item name='userTypeLabel' label={<span style={{ color: '#666' }}> 贷款用户类型</span>}>
              <Input aria-label='贷款用户类型' disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='amount'
              label={<span style={{ color: '#666' }}> 贷款金额</span>}
              rules={[
                { required: true, message: '请输入贷款金额' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve()
                    const error = validateLoanAmount(
                      value,
                      productData?.minCreditAmount,
                      productData?.maxCreditAmount
                    )
                    return error ? Promise.reject(new Error(error)) : Promise.resolve()
                  }
                }
              ]}
              extra={<span style={{ color: '#999', fontSize: 12 }}>贷款金额：{productData?.minCreditAmount ?? '--'}万元 ~ {productData?.maxCreditAmount ?? '--'}万元</span>}
            >
              <Input placeholder='请输入贷款金额' addonAfter='元' />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='term'
              label={<span style={{ color: '#666' }}> 贷款期限</span>}
              rules={[
                { required: true, message: '请输入贷款期限' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve()
                    if (!/^\d+$/.test(String(value)) || Number(value) <= 0) {
                      return Promise.reject(new Error('贷款期限必须为正整数'))
                    }
                    const maximum = Number(productData?.maxLoanTerms)
                    if (Number.isFinite(maximum) && maximum > 0 && Number(value) > maximum) {
                      return Promise.reject(new Error('贷款期限必须符合期限范围！'))
                    }
                    return Promise.resolve()
                  }
                }
              ]}
              extra={<span style={{ color: '#999', fontSize: 12 }}>最长期限：{productData?.maxLoanTerms ?? '--'} {productData?.loanTermsUnitDesc || ''}</span>}
            >
              <Input placeholder='请输入贷款期限' addonAfter={productData?.loanTermsUnitDesc || ''} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='usageInf'
              label={<span style={{ color: '#666' }}> 贷款用途</span>}
              rules={[{ required: true, message: '请选择贷款用途' }]}
            >
              <Select aria-label='贷款用途' placeholder='请选择' disabled={usageOptions.length === 1}>
                {usageOptions.map(option => (
                  <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='orgNos'
              label={<span style={{ color: '#666' }}> 意向机构</span>}
              getValueFromEvent={normalizeOrgValues}
            >
              <Select
                aria-label='意向机构'
                mode='multiple'
                placeholder='请选择（最多3个）'
                onPopupScroll={handleOrgPopupScroll}
                notFoundContent={orgLoading ? <Spin size='small' /> : '暂无机构'}
                filterOption={false}
                disabled={Boolean(fixedOrg)}
              >
                {selectOrgOptions.map(org => (
                  <Select.Option key={org.orgNo} value={org.orgNo}>{org.orgName || org.orgNo}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <h3 style={{ borderBottom: '2px solid #1890ff', display: 'inline-block', paddingBottom: 5, marginBottom: 20, marginTop: 10, color: '#333', fontSize: 16, fontWeight: 'bold' }}>
          {userType === '1' ? '企业信息' : '个人信息'}
        </h3>

        {userType === '1' ? (
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name='enterpriseName' label={<span style={{ color: '#666' }}> 企业名称</span>} rules={[{ required: true, message: '请输入企业名称' }]}>
                <Input placeholder='请输入企业名称' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='taxId' label={<span style={{ color: '#666' }}> 企业税号</span>} rules={[{ required: true, message: '请输入企业税号' }]}>
                <Input placeholder='请输入企业税号' suffix={<Popover content='请确保企业在概而论中记账，并拥有完整的财务数据，若不满足，则无法提交申请！'><span style={{ color: '#1890ff' }}>?</span></Popover>} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='legalPersonName' label={<span style={{ color: '#666' }}> 法人姓名</span>} rules={[{ required: true, message: '请输入法人姓名' }]}>
                <Input placeholder='请输入法人姓名' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='legalPersonId' label={<span style={{ color: '#666' }}> 法人身份证</span>} rules={[
                { required: true, message: '请输入法人身份证号码' },
                { pattern: /^[1-9]\d{5}(18|19|20)\d{2}((0[1-9])|(1[0-2]))(([0-2][1-9])|10|20|30|31)\d{3}[0-9Xx]$/, message: '请输入正确的身份证号码' }
              ]}>
                <Input placeholder='请输入法人身份证号码' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='mobile' label={<span style={{ color: '#666' }}> 法人手机号</span>} rules={[
                { required: true, message: '请输入法人手机号' },
                { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' }
              ]}>
                <Input placeholder='请输入法人手机号' />
              </Form.Item>
            </Col>
            {renderVerifyCodeItem()}
          </Row>
        ) : (
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name='borrowerName' label={<span style={{ color: '#666' }}> 贷款人名称</span>} rules={[{ required: true, message: '请输入客户名称' }]}>
                <Input placeholder='请输入客户名称' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='borrowerId' label={<span style={{ color: '#666' }}> 贷款人身份证</span>} rules={[
                { required: true, message: '请输入贷款人身份证号码' },
                { pattern: /^[1-9]\d{5}(18|19|20)\d{2}((0[1-9])|(1[0-2]))(([0-2][1-9])|10|20|30|31)\d{3}[0-9Xx]$/, message: '请输入正确的身份证号码' }
              ]}>
                <Input placeholder='请输入贷款人身份证号码' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='borrowerMobile' label={<span style={{ color: '#666' }}> 贷款人手机号</span>} rules={[
                { required: true, message: '请输入贷款人手机号' },
                { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' }
              ]}>
                <Input placeholder='请输入贷款人手机号' />
              </Form.Item>
            </Col>
            {renderVerifyCodeItem()}
          </Row>
        )}

        <Form.Item
          name='agreement'
          valuePropName='checked'
          wrapperCol={{ span: 24 }}
          rules={[{
            validator: (_, value) => value
              ? Promise.resolve()
              : Promise.reject(new Error('请阅读并同意协议'))
          }]}
          style={{ marginBottom: 24, textAlign: 'center' }}
        >
          <Checkbox>
            我已认真阅读并同意 <a href='#loan-application-protocol' onClick={event => {
              event.preventDefault()
              setProModalVisible(true)
            }} style={{ color: '#1890ff' }}>《企业数据授权协议》</a>
          </Checkbox>
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 20, paddingBottom: 20 }}>
          <Button size='large' onClick={handleClose} disabled={submitting} style={{ width: 120 }}>取消</Button>
          <Button type='primary' size='large' htmlType='submit' loading={submitting} disabled={submitDisabled || submitting} style={{ width: 120 }}>
            提交申请
          </Button>
        </div>
      </Form>

      <TaxAuthModal
        visible={taxAuthVisible}
        onCancel={handleTaxCancel}
        onSuccess={handleTaxSuccess}
        data={{
          custName: pendingValues?.enterpriseName,
          custCerNo: pendingValues?.taxId,
          cpCellPhone: pendingValues?.mobile
        }}
      />
      <Modal title='请等待' visible={verifyingVisible} footer={null} closable={false} width={400} centered destroyOnClose>
        <div style={{ textAlign: 'center', padding: '30px 0' }}>
          <Spin size='large' />
          <div style={{ marginTop: 20, fontSize: 16 }}>正在验证身份，请稍后...</div>
        </div>
      </Modal>
      <Protocol modalVisible={proModalVisible} onCancel={() => setProModalVisible(false)} />
    </Modal>
  )
})

export default LoanApplyModal
